import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Check, Clock, ShieldCheck, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicShell, Loading } from "@/components/shared";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PlaybookDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [paying, setPaying] = useState(false);
  const { data: p, isLoading, refetch } = useQuery({ queryKey: ["playbook", slug], queryFn: async () => (await api.get(`/playbooks/${slug}`)).data });

  if (isLoading) return <PublicShell><Loading /></PublicShell>;
  if (!p) return <PublicShell><div className="py-24 text-center text-slate-500">Not found.</div></PublicShell>;

  const download = async () => {
    try { const { data } = await api.get(`/playbooks/${slug}/download`); window.open(data.url, "_blank"); }
    catch (e) { toast.error(e.response?.data?.detail || "File not ready yet"); }
  };

  const buy = async () => {
    if (!user) { toast("Create a free account to buy — it's how we unlock your download."); navigate("/login"); return; }
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load the payment window"); setPaying(false); return; }
      const { data: order } = await api.post(`/playbooks/${slug}/order`);
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency,
        name: p.title, description: "Playbook — personal use", order_id: order.order_id,
        prefill: { name: order.name, email: order.email },
        theme: { color: "#059669" },
        handler: async (res) => {
          try {
            await api.post(`/playbooks/verify`, {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Purchased — your download is ready.");
            refetch();
          } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payments are opening soon — join the newsletter to be first to know.");
      setPaying(false);
    }
  };

  return (
    <PublicShell>
      <section className="bg-navy grain"><div className="relative mx-auto max-w-3xl px-5 py-16 lg:px-8">
        <button onClick={() => navigate("/playbooks")} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white" data-testid="playbook-back"><ArrowLeft className="h-4 w-4" /> All playbooks</button>
        {!p.is_bundle && <p className="text-xs font-bold uppercase tracking-wider text-emerald">Module {p.module}</p>}
        <h1 className="mt-2 font-heading text-3xl font-bold text-white sm:text-4xl" data-testid="playbook-title">{p.title}</h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-slate-300">{p.description}</p>
      </div></section>

      <section className="mx-auto max-w-3xl px-5 py-12 lg:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          {p.status === "available" || p.owned ? (
            <>
              <p className="font-heading text-4xl font-bold text-navy">{p.owned ? "Yours" : inr(p.price)}</p>
              <p className="mt-2 text-sm text-slate-500">One-time purchase · personal use</p>
              {p.owned ? (
                <Button onClick={download} className="mt-6 h-12 rounded-md bg-emerald px-8 font-semibold text-white hover:bg-emerald-700" data-testid="playbook-download-btn"><Download className="mr-2 h-5 w-5" /> Download</Button>
              ) : (
                <Button onClick={buy} disabled={paying} className="mt-6 h-12 rounded-md bg-emerald px-8 font-semibold text-white hover:bg-emerald-700" data-testid="playbook-buy-btn">{paying ? "…" : `Buy for ${inr(p.price)}`}</Button>
              )}
              <p className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4 text-emerald" /> For personal use only. Not licensed for resale or redistribution.</p>
            </>
          ) : (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-sm font-bold text-amber-700"><Clock className="h-4 w-4" /> {p.available_date ? `Available ${p.available_date}` : "Coming soon"}</span>
              <p className="mt-4 text-slate-600">This one isn't out yet. Get the daily and I'll tell you when it lands.</p>
              <Button onClick={() => navigate("/daily")} variant="outline" className="mt-5 rounded-md" data-testid="playbook-daily-btn">Get the daily</Button>
            </>
          )}
        </div>

        {/* Course band */}
        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center" data-testid="playbook-course-band">
          {p.is_bundle ? (
            <p className="text-[15px] text-slate-700">Want it on video, with the templates and all future updates? <button onClick={() => navigate("/sales-mastery")} className="font-semibold text-emerald hover:underline">Ultimate Sales Mastery is {inr(p.course_price)}.</button></p>
          ) : (
            <p className="text-[15px] text-slate-700">This is module {p.module} of Ultimate Sales Mastery. <button onClick={() => navigate("/sales-mastery")} className="font-semibold text-emerald hover:underline">The full course is {inr(p.course_price)}.</button></p>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
